import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styles from './bodywon.module.css';
import WriteReviewModal from './WriteReviewModal';
import RatingBreakdownPopover from './RatingBreakdownPopover';
import { useLoginPrompt } from '../context/LoginPromptContext';
import { isCartUserLoggedIn } from '../utils/cartApi';
import { fetchProductReviewsForProduct, fetchProductRatingSummary, invalidateProductRatingSummary } from '../utils/productReviewApi';

const EMPTY_DISTRIBUTION = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

const ProductReviewStars = ({
  product,
  variant = 'card',
  showReviewCount = true,
  showWriteLink = false,
  onReviewSubmitted,
  onSeeReviews,
}) => {
  const onRequireLogin = useLoginPrompt();
  const [displayRating, setDisplayRating] = useState(Number(product?.rating) || 0);
  const [displayCount, setDisplayCount] = useState(Number(product?.reviewCount) || 0);
  const [distribution, setDistribution] = useState(EMPTY_DISTRIBUTION);
  const [hoverRating, setHoverRating] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [selectedRating, setSelectedRating] = useState(5);
  const breakdownHideTimer = useRef(null);

  const applySummary = useCallback((summary) => {
    setDisplayRating(summary.rating);
    setDisplayCount(summary.count);
    setDistribution(summary.distribution ?? EMPTY_DISTRIBUTION);
  }, []);

  const refreshSummary = useCallback(async () => {
    if (!product?.id) return null;
    invalidateProductRatingSummary(product.id);
    try {
      const summary = await fetchProductRatingSummary(product.id);
      applySummary(summary);
      const { items } = await fetchProductReviewsForProduct(product.id);
      onReviewSubmitted?.(summary, items);
      return summary;
    } catch {
      return null;
    }
  }, [product?.id, onReviewSubmitted, applySummary]);

  useEffect(() => {
    if (!product?.id) return undefined;

    let active = true;
    fetchProductRatingSummary(product.id).then((summary) => {
      if (!active) return;
      applySummary(summary);
    });

    return () => {
      active = false;
    };
  }, [product?.id, applySummary]);

  const openReview = (ratingValue) => {
    if (!product?.id) return;

    if (!isCartUserLoggedIn()) {
      toast.warning('Please login to write a review.');
      onRequireLogin?.();
      return;
    }

    setSelectedRating(ratingValue);
    setShowModal(true);
  };

  const handleStarClick = (e, value) => {
    e.preventDefault();
    e.stopPropagation();
    openReview(value);
  };

  const stopBubble = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const openBreakdown = () => {
    if (variant !== 'detail' || displayCount <= 0) return;
    if (breakdownHideTimer.current) {
      clearTimeout(breakdownHideTimer.current);
      breakdownHideTimer.current = null;
    }
    setShowBreakdown(true);
  };

  const scheduleCloseBreakdown = () => {
    if (breakdownHideTimer.current) clearTimeout(breakdownHideTimer.current);
    breakdownHideTimer.current = setTimeout(() => {
      setShowBreakdown(false);
      breakdownHideTimer.current = null;
    }, 150);
  };

  useEffect(() => () => {
    if (breakdownHideTimer.current) clearTimeout(breakdownHideTimer.current);
  }, []);

  const handleSeeReviews = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBreakdown(false);
    onSeeReviews?.();
  };

  const filledCount = hoverRating || Math.round(displayRating);
  const showSummary = displayCount > 0;
  const starsClass =
    variant === 'detail'
      ? `${styles.stars} ${styles.starsInteractive}`
      : `${styles.featureRating} ${styles.starsInteractive}`;

  return (
    <>
      <div
        className={styles.productReviewStarsWrap}
        onClick={stopBubble}
        onMouseDown={stopBubble}
      >
        <div
          className={`${starsClass}${variant === 'detail' && showSummary ? ` ${styles.ratingBreakdownHoverZone}` : ''}`}
          onMouseEnter={openBreakdown}
          onMouseLeave={() => {
            setHoverRating(0);
            scheduleCloseBreakdown();
          }}
          role="presentation"
        >
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              className={styles.reviewStarBtn}
              onMouseEnter={() => setHoverRating(value)}
              onClick={(e) => handleStarClick(e, value)}
              aria-label={`Rate ${value} star${value > 1 ? 's' : ''}`}
            >
              {value <= filledCount ? <FaStar /> : <FaRegStar />}
            </button>
          ))}
          {showSummary ? (
            <>
              <span className={styles.averageRatingText}>{displayRating.toFixed(1)}</span>
              {showReviewCount ? (
                <span className={styles.reviewCount}>({displayCount})</span>
              ) : null}
            </>
          ) : null}
          {variant === 'detail' && showSummary ? (
            <RatingBreakdownPopover
              open={showBreakdown}
              onClose={() => setShowBreakdown(false)}
              onMouseEnter={openBreakdown}
              onMouseLeave={scheduleCloseBreakdown}
              closeOnOutsideClick={false}
              rating={displayRating}
              count={displayCount}
              distribution={distribution}
              onSeeReviews={handleSeeReviews}
            />
          ) : null}
        </div>
        {showWriteLink ? (
          <button
            type="button"
            className={styles.writeReviewLink}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openReview(Math.max(1, Math.round(displayRating)) || 5);
            }}
          >
            Write a review
          </button>
        ) : null}
      </div>

      <WriteReviewModal
        show={showModal}
        onHide={() => setShowModal(false)}
        product={product}
        initialRating={selectedRating}
        onSubmitted={refreshSummary}
      />
    </>
  );
};

export default ProductReviewStars;
